import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Capture AG Grid layout state (column order/width/visibility/sort/pinning
 * + filter model) into a serializable payload.
 *
 * The payload deliberately stores the raw ``getColumnState()`` array so
 * pinning, hiding, sorting, width, and order all round-trip through
 * ``applyColumnState`` without bespoke encoding here. ``version`` lets us
 * migrate the shape later without breaking older saved rows.
 *
 * Returns ``null`` when the grid isn't ready — callers should treat that
 * as "no payload to save."
 *
 * @param {Object} gridApi - AG Grid API ref (gridRef.current.api).
 * @returns {Object|null} ``{ version, columnState, filterModel }``.
 */
export const captureGridState = (gridApi) => {
  if (!gridApi || typeof gridApi.getColumnState !== 'function') return null;
  return {
    version: 1,
    columnState: gridApi.getColumnState(),
    filterModel: typeof gridApi.getFilterModel === 'function'
      ? gridApi.getFilterModel()
      : {},
  };
};

/**
 * Restore a previously-captured payload onto an AG Grid instance.
 *
 * Resilient to schema drift in three ways:
 *
 *  1. **Removed columns** — ``applyColumnState`` silently ignores ``colId``
 *     entries that no longer exist in the live grid.
 *  2. **Added columns** — columns present in the grid but not in the saved
 *     state are left at whatever the columnDefs declared (default
 *     visibility, position-after-saved-columns, columnDef pinning). We
 *     intentionally do NOT pass ``defaultState`` so newly-added columns
 *     get the developer's intended defaults rather than being force-hidden.
 *  3. **Stale filter colIds** — filter entries on removed columns are
 *     stripped before ``setFilterModel`` so we don't trip AG Grid's
 *     "unknown column" warning path.
 *
 * Each step is wrapped in try/catch so a failure restoring (say) the
 * filter model doesn't prevent the column layout from being applied —
 * partial restore is strictly better than no restore.
 *
 * @param {Object} gridApi - AG Grid API ref.
 * @param {Object} payload - Output of ``captureGridState``.
 */
export const applyGridState = (gridApi, payload) => {
  if (!gridApi || !payload) return;

  if (Array.isArray(payload.columnState) && typeof gridApi.applyColumnState === 'function') {
    try {
      gridApi.applyColumnState({
        state: payload.columnState,
        // applyOrder reorders the columns listed in `state` to match
        // their order in the array; columns not in `state` keep their
        // current relative position.
        applyOrder: true,
      });
    } catch (e) {
      // Don't let a malformed columnState block the filter restore.
      // eslint-disable-next-line no-console
      console.warn('[useGridConfigs] applyColumnState failed', e);
    }
  }

  if (payload.filterModel && typeof gridApi.setFilterModel === 'function') {
    const liveFilterModel = stripStaleFilters(gridApi, payload.filterModel);
    try {
      gridApi.setFilterModel(liveFilterModel);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useGridConfigs] setFilterModel failed', e);
    }
  }
};

/**
 * Drop filter entries whose ``colId`` no longer exists on the live grid.
 *
 * AG Grid will accept stale colIds but emits a console warning per
 * unknown column and queues a filter on a column nothing can interact
 * with — both noisy and confusing. Pre-filtering keeps the restore
 * silent and accurate.
 */
// ``getColumns()`` is the AG Grid v32+ name; ``getAllColumns()`` is the older
// one. Some lite builds expose neither, so callers must handle ``null``.
const columnsAccessor = (gridApi) => {
  if (typeof gridApi.getColumns === 'function') return gridApi.getColumns;
  if (typeof gridApi.getAllColumns === 'function') return gridApi.getAllColumns;
  return null;
};

/**
 * The grid's live column ids as one order-independent string: ``''`` while it
 * has none, ``null`` when it cannot be asked (lite builds).
 *
 * A grid whose columnDefs are built from fetched data is ready long before its
 * data columns exist, and a saved layout applied then has nothing to land on.
 * PamGrid can also hold an actions or selection column in the meantime, so
 * "has a column" is not enough. The restore therefore applies when it first
 * sees columns, and again whenever this string changes, until the user acts.
 */
const liveColumnKey = (gridApi) => {
  const getCols = columnsAccessor(gridApi);
  if (!getCols) return null;
  return (getCols.call(gridApi) || [])
    .map((c) => (typeof c.getColId === 'function' ? c.getColId() : c.colId))
    .sort()
    .join('|');
};

/**
 * Whether the grid has any rows. A grid api that cannot be asked counts as
 * having them, so it keeps the restore-once behaviour.
 */
const gridHasRows = (gridApi) => {
  if (typeof gridApi.forEachNode !== 'function') return true;
  let any = false;
  gridApi.forEachNode(() => { any = true; });
  return any;
};

/**
 * The filter model to apply again once rows arrive, or ``null``.
 *
 * AG Grid's Set Filter lists a column's values from the rows, and drops a Set
 * Filter model applied while the grid has none. A grid with static columns is
 * restored at grid ready, before its data loads, so its saved Set Filters were
 * lost. A filter applied with no rows is therefore applied again on the first
 * ``rowDataUpdated`` that brings rows.
 */
const filterToApplyOnRows = (gridApi, payload) => {
  if (!gridApi || !payload || typeof gridApi.setFilterModel !== 'function') return null;
  const live = stripStaleFilters(gridApi, payload.filterModel);
  if (Object.keys(live).length === 0) return null;
  return gridHasRows(gridApi) ? null : live;
};

const stripStaleFilters = (gridApi, filterModel) => {
  if (!filterModel || typeof filterModel !== 'object') return {};
  // Pass the model through untouched when the grid cannot list its columns.
  const getCols = columnsAccessor(gridApi);
  if (!getCols) return filterModel;
  const cols = getCols.call(gridApi) || [];
  const liveColIds = new Set(cols.map((c) => (typeof c.getColId === 'function' ? c.getColId() : c.colId)));
  const filtered = {};
  for (const [colId, value] of Object.entries(filterModel)) {
    if (liveColIds.has(colId)) filtered[colId] = value;
  }
  return filtered;
};

// ---------- Local stash (survive-refresh layer) ----------------------------

/**
 * localStorage key for the scratch / unsaved layout for one (app, gridId).
 *
 * Scoped only by app+gridId — not by user email — because localStorage is
 * already partitioned per browser profile, and resolving the user's email
 * synchronously here is awkward (the SWA principal arrives via an async
 * fetch). The named-config layer in Snowflake is the durable, per-user
 * source of truth; the stash is intentionally a thin browser-local cache.
 */
const stashKey = (app, gridId) => `1823:gridcfg:${app}:${gridId}`;

/** Safe wrapper — guards SSR, quota, and corrupt payloads. */
const readStash = (app, gridId) => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(stashKey(app, gridId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.payload) return null;
    return parsed;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[useGridConfigs] readStash failed', e);
    return null;
  }
};

const writeStash = (app, gridId, payload, activeName) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(
      stashKey(app, gridId),
      JSON.stringify({ payload, activeName: activeName || null }),
    );
  } catch (e) {
    // Quota or serialization issue — non-fatal; the server-side named
    // configs still work, the user just loses the refresh-restore.
    // eslint-disable-next-line no-console
    console.warn('[useGridConfigs] writeStash failed', e);
  }
};

const clearStash = (app, gridId) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(stashKey(app, gridId));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[useGridConfigs] clearStash failed', e);
  }
};

// Debounce interval for stash writes. Column resize fires per-pixel; this
// keeps localStorage writes bounded without losing the final value.
const STASH_DEBOUNCE_MS = 200;

// AG Grid event ``source`` values that are the grid or the app changing
// layout, not the user. PamGrid autosizes every column on grid ready and on
// first render, and applying a saved layout fires column events too. If
// those counted, every page load would stash the current layout, and the
// stash would then win over the user's saved default. A denylist rather than
// an allowlist, so a user-driven source this list doesn't know still counts.
const PROGRAMMATIC_SOURCES = new Set([
  'api',
  'autosizeColumns',
  'autosizeColumnHeaderHeight',
  'sizeColumnsToFit',
  'flex',
  'gridInitializing',
  'gridOptionsChanged',
  'rowDataUpdated',
  'rowModelUpdated',
  'cellDataTypeInferred',
  'viewportSizeFeature',
  'alignedGridChanged',
  'pivotChart',
  'filterChanged',
  'filterDestroyed',
  'rowNumbersService',
]);

// Decided by the event's ``source`` alone, never by timing, so a real gesture
// right after load is never dropped. ``api`` is programmatic for every event
// type: AG Grid reports the hook's own restore, PamGrid's autosize and an
// app's ``setFilterModel`` that way. A user-driven call through the API
// reports itself instead: PamGrid's Clear Filters calls
// ``api.onFilterChanged('columnFilter')``, and so should an app's own
// clear-filters button.
const isProgrammaticEvent = (event) =>
  Boolean(event && typeof event.source === 'string' && PROGRAMMATIC_SOURCES.has(event.source));

/**
 * Per-user grid layout persistence backed by the shared
 * ``/grid-configs`` blueprint on the PAM servers.
 *
 * Identity is server-derived from the SWA principal — callers never pass
 * an email. The hook manages the full lifecycle: fetch on mount, apply
 * default (or local stash, see below) once the grid becomes ready, expose
 * save / delete / setDefault / applyConfig commands the toolbar UI uses.
 *
 * **Local scratch layer**: every column / sort / filter change is mirrored
 * into ``localStorage`` so an in-flight, unsaved layout survives a page
 * refresh. On mount the stash takes precedence over the server default —
 * the user's most recent gesture is the most authoritative signal. To go
 * back to the server default, callers can invoke ``resetLocal()``.
 *
 * **Grid-ready race**: configs may load before the grid API ref resolves.
 * Refs don't trigger re-renders, so the auto-apply effect alone can't
 * fire on grid readiness. Consumers MUST wire the returned ``onGridReady``
 * callback to ``<PamGrid onGridReady=… />`` so the hook learns when the
 * API is live and can replay the default at that moment.
 *
 * **Wiring state-change capture**: consumers wire ``onGridStateChanged``
 * to AG Grid's column / sort / filter events so the stash stays current.
 * Spread ``stashBindings`` onto ``<PamGrid agGridProps=… />`` for the
 * column events that PamGrid forwards via the agGridProps passthrough.
 *
 * @param {Object} api - axios-shaped client (``createAppTransport``). Required.
 * @param {Object} options
 * @param {string} options.app - URL slug for the host app ('recon', 'blotter', …).
 * @param {string} options.gridId - Per-app grid identifier ('main', 'exclusions', …).
 * @param {Object} [options.gridApiRef] - React ref whose ``.current``
 *   exposes the AG Grid API. Used to auto-apply the default config once
 *   the grid is ready. Pass the same ref you give to ``<PamGrid ref=…/>``.
 * @param {boolean} [options.persistLocal=true] - Enable the localStorage
 *   scratch layer. Disable in tests / kiosks where refresh-survives-unsaved
 *   is undesirable.
 * @returns {Object} Hook surface (see properties below).
 */
export const useGridConfigs = (api, { app, gridId, gridApiRef, persistLocal = true } = {}) => {
  const [configs, setConfigs] = useState([]);
  const [activeName, setActiveName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [readyTick, setReadyTick] = useState(0);
  // Once an auto-apply (stash or default) has happened, don't fire again
  // for this (app, gridId). A background reload mustn't snap the user
  // away from a layout they just chose.
  const autoAppliedRef = useRef(false);
  // The live columns at the last auto-apply, and whether the user has done
  // anything since: see applyPendingLayout.
  const appliedColumnsRef = useRef(null);
  const userActedRef = useRef(false);
  // A restored filter that went on while the grid had no rows, to apply again
  // when they arrive: see onRowDataUpdated.
  const pendingFilterRef = useRef(null);
  // The (app, gridId) whose state the identity-reset effect has last cleared.
  // The configs and the refs above belong to it, and to no other.
  const identityKey = `${app}\u0000${gridId}`;
  const identityResetRef = useRef(null);
  // Stable read of activeName for the debounced stash writer that can't
  // close over the latest state value through useCallback.
  const activeNameRef = useRef(null);
  useEffect(() => { activeNameRef.current = activeName; }, [activeName]);
  const stashTimerRef = useRef(null);

  const reload = useCallback(async () => {
    if (!api || !app || !gridId) return;
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/grid-configs?app=${encodeURIComponent(app)}&gridId=${encodeURIComponent(gridId)}`);
      const list = (data && Array.isArray(data.configs)) ? data.configs : [];
      setConfigs(list);
      // Functional setter so reload() called after applyConfig() doesn't
      // overwrite the user's selection with a stale closure value.
      const def = list.find((c) => c.isDefault);
      if (def) setActiveName((prev) => prev || def.name);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, app, gridId]);

  useEffect(() => { reload(); }, [reload]);

  // Reset the auto-applied guard, the activeName, AND the configs list
  // when the grid identity changes. Without clearing configs, the
  // auto-apply effect would fire one tick after the (app, gridId)
  // change with the prior grid's configs still in state — applying the
  // OLD default to the NEW grid and flipping autoAppliedRef true before
  // the new fetch returns. Clearing here ensures the auto-apply runs
  // exactly once per identity, with that identity's data.
  useEffect(() => {
    autoAppliedRef.current = false;
    appliedColumnsRef.current = null;
    userActedRef.current = false;
    pendingFilterRef.current = null;
    identityResetRef.current = identityKey;
    setActiveName(null);
    setConfigs([]);
  }, [app, gridId, identityKey]);

  const onGridReady = useCallback(() => {
    setReadyTick((t) => t + 1);
  }, []);

  // Auto-apply: prefer the local stash, fall back to the server default.
  // Re-runs on configs load or grid readiness.
  //
  // NOTE: app/gridId are intentionally NOT in the dep array. If they
  // were, this effect would fire on the same render as the reset effect
  // (above) — but its closure would still hold the PRIOR grid's configs
  // (state from setConfigs([]) hasn't committed yet), and we'd apply the
  // old default to the new grid before the new reload resolves. Driving
  // off configs instead means we wait for the reset's setConfigs([]) to
  // commit, see an empty list, no-op, and then fire again when the new
  // gridId's reload populates configs. app/gridId are still read inside
  // via closure — they'll have the latest values when the effect runs.
  //
  // It also waits for columns. A grid that builds them from fetched data is
  // ready before it has any, so with none the restore stays pending, and
  // ``onNewColumnsLoaded`` runs it again when they arrive. Once applied it
  // applies again only if the live columns have changed (PamGrid's actions or
  // selection column can come before the data columns) and the user has not
  // acted since, so a later column reload never applies a layout over theirs.
  const applyPendingLayout = () => {
    // A columns-loaded event can fire while a new (app, gridId) renders, before
    // the reset effect has cleared the previous grid's configs and refs. Wait:
    // that reset re-renders, and the effect below then applies for the new grid.
    if (identityResetRef.current !== identityKey) return;
    if (!gridApiRef) return;
    const gridApi = gridApiRef.current && gridApiRef.current.api;
    if (!gridApi) return;
    const columns = liveColumnKey(gridApi);
    if (columns === '') return;
    if (autoAppliedRef.current) {
      if (userActedRef.current || columns === null || columns === appliedColumnsRef.current) return;
    }

    if (persistLocal && app && gridId) {
      const stash = readStash(app, gridId);
      if (stash) {
        applyGridState(gridApi, stash.payload);
        pendingFilterRef.current = filterToApplyOnRows(gridApi, stash.payload);
        if (stash.activeName) setActiveName(stash.activeName);
        autoAppliedRef.current = true;
        appliedColumnsRef.current = columns;
        return;
      }
    }

    const def = configs.find((c) => c.isDefault);
    if (!def) return;
    applyGridState(gridApi, def.config);
    pendingFilterRef.current = filterToApplyOnRows(gridApi, def.config);
    setActiveName(def.name);
    autoAppliedRef.current = true;
    appliedColumnsRef.current = columns;
  };

  // The latest render's closure, for ``onNewColumnsLoaded``. Calling it does
  // not touch state, so a columns-loaded event never re-renders the host. A
  // grid with no saved layout would otherwise rebuild its columnDefs on that
  // render and fire the event again.
  const applyPendingLayoutRef = useRef(applyPendingLayout);
  applyPendingLayoutRef.current = applyPendingLayout;

  const onNewColumnsLoaded = useCallback(() => {
    applyPendingLayoutRef.current();
  }, []);

  // Apply a restored filter again once the grid has rows (see
  // ``filterToApplyOnRows``). Once only, and never after the user has changed
  // a filter, which clears it. Like ``onNewColumnsLoaded`` it goes through the
  // latest render's closure and sets no state; the re-apply's
  // ``filterChanged`` has source ``api`` and so is not stashed.
  const applyPendingFilter = () => {
    // As in applyPendingLayout: a data update during a new (app, gridId)'s
    // render must not put the previous grid's filter on the new one.
    if (identityResetRef.current !== identityKey) return;
    const pending = pendingFilterRef.current;
    if (!pending) return;
    const gridApi = gridApiRef && gridApiRef.current && gridApiRef.current.api;
    if (!gridApi || !gridHasRows(gridApi)) return;
    pendingFilterRef.current = null;
    try {
      gridApi.setFilterModel(pending);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useGridConfigs] setFilterModel failed', e);
    }
  };
  const applyPendingFilterRef = useRef(applyPendingFilter);
  applyPendingFilterRef.current = applyPendingFilter;

  const onRowDataUpdated = useCallback(() => {
    applyPendingFilterRef.current();
  }, []);

  // The layout to save. While a restored filter waits for rows, AG Grid
  // reports it as dropped, so save the filter that is still to come.
  const captureLayout = (gridApi) => {
    const payload = captureGridState(gridApi);
    if (payload && pendingFilterRef.current) payload.filterModel = pendingFilterRef.current;
    return payload;
  };
  const captureLayoutRef = useRef(captureLayout);
  captureLayoutRef.current = captureLayout;

  useEffect(() => {
    applyPendingLayout();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs, readyTick, gridApiRef, persistLocal]);

  /**
   * Wire to AG Grid column / sort / filter events to keep the local stash
   * in sync. Debounced so a column resize (per-pixel events) writes
   * localStorage at most every ``STASH_DEBOUNCE_MS``. Events from a
   * programmatic ``source`` (autosize, ``api``, grid setup) are ignored; a
   * call with no event (a direct call) still stashes.
   */
  const onGridStateChanged = useCallback((event) => {
    if (isProgrammaticEvent(event)) return;
    userActedRef.current = true;
    // Only the user's own filter replaces a restored one still waiting for
    // rows. A sort or resize keeps it; the quick filter is not in the model.
    if (!event || (event.type === 'filterChanged' && event.source !== 'quickFilter')) {
      pendingFilterRef.current = null;
    }
    if (!persistLocal || !app || !gridId) return;
    const gridApi = gridApiRef && gridApiRef.current && gridApiRef.current.api;
    if (!gridApi) return;
    if (stashTimerRef.current) clearTimeout(stashTimerRef.current);
    stashTimerRef.current = setTimeout(() => {
      const payload = captureLayoutRef.current(gridApi);
      if (payload) writeStash(app, gridId, payload, activeNameRef.current);
    }, STASH_DEBOUNCE_MS);
  }, [app, gridId, gridApiRef, persistLocal]);

  // Flush any pending stash write on unmount so a refresh triggered mid-debounce
  // doesn't drop the latest user gesture.
  useEffect(() => {
    return () => {
      if (stashTimerRef.current) {
        clearTimeout(stashTimerRef.current);
        // Read the grid as it is now, at cleanup, on purpose: an identity change flushes the
        // old grid's pending layout into the old key. A copy taken when the effect ran could
        // be a grid that has been replaced since.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const gridApi = gridApiRef && gridApiRef.current && gridApiRef.current.api;
        if (gridApi && persistLocal && app && gridId) {
          const payload = captureLayoutRef.current(gridApi);
          if (payload) writeStash(app, gridId, payload, activeNameRef.current);
        }
      }
    };
  }, [app, gridId, gridApiRef, persistLocal]);

  /**
   * Persist the current grid state under ``name``. ``isDefault: true``
   * also flips every other saved config for this (app, gridId) to
   * non-default on the server in the same call.
   */
  const saveConfig = useCallback(async (name, { isDefault = false, gridApi } = {}) => {
    const liveApi = gridApi || (gridApiRef && gridApiRef.current && gridApiRef.current.api);
    const payload = captureLayoutRef.current(liveApi);
    if (!payload) throw new Error('Grid is not ready');
    if (!name || !name.trim()) throw new Error('name is required');
    await api.post('/grid-configs', {
      app,
      gridId,
      name: name.trim(),
      config: payload,
      isDefault,
    });
    setActiveName(name.trim());
    // Refresh the stash so the activeName field reflects the new name —
    // without this, refresh would restore the same layout but show no
    // active config in the menu.
    if (persistLocal && app && gridId) {
      writeStash(app, gridId, payload, name.trim());
    }
    await reload();
  }, [api, app, gridId, gridApiRef, reload, persistLocal]);

  /**
   * Apply a named, already-saved config to the live grid.
   */
  const applyConfig = useCallback((name) => {
    const target = configs.find((c) => c.name === name);
    if (!target) return;
    const gridApi = gridApiRef && gridApiRef.current && gridApiRef.current.api;
    applyGridState(gridApi, target.config);
    pendingFilterRef.current = filterToApplyOnRows(gridApi, target.config);
    setActiveName(name);
    autoAppliedRef.current = true;
    userActedRef.current = true;
    // Eagerly write the stash so a refresh-before-debounce still keeps
    // the just-applied config (the AG Grid events will write again
    // momentarily, but this guarantees the stash is current immediately).
    if (persistLocal && app && gridId && gridApi) {
      const payload = captureLayoutRef.current(gridApi);
      if (payload) writeStash(app, gridId, payload, name);
    }
  }, [configs, gridApiRef, app, gridId, persistLocal]);

  const deleteConfig = useCallback(async (name) => {
    await api.delete('/grid-configs', { data: { app, gridId, name } });
    if (activeName === name) setActiveName(null);
    await reload();
  }, [api, app, gridId, activeName, reload]);

  const setDefault = useCallback(async (name) => {
    await api.post('/grid-configs/default', { app, gridId, name });
    await reload();
  }, [api, app, gridId, reload]);

  /**
   * Clear the local stash and re-apply the server default. Use this for
   * a "reset view" button — it discards unsaved changes and snaps the
   * grid back to the default layout (or to columnDef defaults if no
   * default is saved).
   */
  const resetLocal = useCallback(() => {
    if (app && gridId) clearStash(app, gridId);
    userActedRef.current = true;
    pendingFilterRef.current = null;
    const gridApi = gridApiRef && gridApiRef.current && gridApiRef.current.api;
    if (!gridApi) return;
    const def = configs.find((c) => c.isDefault);
    if (def) {
      applyGridState(gridApi, def.config);
      pendingFilterRef.current = filterToApplyOnRows(gridApi, def.config);
      setActiveName(def.name);
    } else {
      // No default — reset columns to their columnDef declarations.
      try {
        if (typeof gridApi.resetColumnState === 'function') gridApi.resetColumnState();
        if (typeof gridApi.setFilterModel === 'function') gridApi.setFilterModel({});
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[useGridConfigs] resetLocal: resetColumnState failed', e);
      }
      setActiveName(null);
    }
  }, [app, gridId, gridApiRef, configs]);

  return {
    configs,
    activeName,
    isLoading,
    error,
    reload,
    saveConfig,
    applyConfig,
    deleteConfig,
    setDefault,
    onGridReady,
    onGridStateChanged,
    resetLocal,
    // Convenience bundle for spreading onto PamGrid's ``agGridProps`` —
    // covers all the AG Grid events that change persisted state, plus
    // ``onNewColumnsLoaded``, which lets the restore wait for columns that
    // are built from fetched data, and ``onRowDataUpdated``, which applies a
    // restored filter again once there are rows for it. Filter changes are
    // bound separately because PamGrid surfaces ``onFilterChanged`` as a
    // top-level prop.
    stashBindings: {
      onColumnMoved: onGridStateChanged,
      onColumnResized: onGridStateChanged,
      onColumnVisible: onGridStateChanged,
      onColumnPinned: onGridStateChanged,
      onSortChanged: onGridStateChanged,
      onNewColumnsLoaded,
      onRowDataUpdated,
    },
  };
};

export default useGridConfigs;
